import React, { useEffect, useState } from 'react';
import ReactModal from 'react-modal';
import { useGetAutomationUserDataMutation } from '../apiSlice';
import Loader from '@Components/Loader';
import { displayToast } from '@Utils/toast';
import {
    Pagination,
    PaginationContent,
    PaginationItem,
    PaginationLink,
    PaginationNext,
    PaginationPrevious,
} from "@Components/shadcn/ui/pagination"

interface UserInputsModalProps {
    isOpen: boolean;
    onClose: () => void;
    automationId: number | null;
}

const UserInputsModal: React.FC<UserInputsModalProps> = ({ isOpen, onClose, automationId }) => {
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [data, setData] = useState<any[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [isDownloading, setIsDownloading] = useState(false);

    const [getAutomationUserData, { isLoading }] = useGetAutomationUserDataMutation();

    useEffect(() => {
        if (isOpen && automationId) {
            fetchData();
        } else {
            // Reset state when closed
            setPage(1);
            setData([]);
            setTotalCount(0);
        }
    }, [isOpen, automationId, page, limit]);

    const fetchData = async () => {
        if (!automationId) return;
        try {
            const response = await getAutomationUserData({
                automation_id: automationId,
                page: page,
                limit: limit
            }).unwrap();

            if (response?.data) {
                setData(response.data || []);
                // Check for total_count in the response root or data if it's an object (but user said data is array)
                // Assuming total_count is a sibling of data
                setTotalCount(response.total_count || response.count || response.data?.length || 0);
            }
        } catch (error: any) {
            console.error("Failed to fetch user data", error);
            displayToast("error", error?.data?.message || "Failed to fetch leads");
        }
    };

    const headers = ['IG Username', 'Name', 'Email', 'Phone Number', 'Created At'];

    const escapeVal = (val: any) => {
        if (val === null || val === undefined) return '';
        const str = String(val);
        if (str.includes(',') || str.includes('"') || str.includes('\n')) {
            return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
    };

    const downloadCSV = async () => {
        try {
            if (!automationId || totalCount === 0) return;

            setIsDownloading(true);

            // Fetch all data for download
            const response = await getAutomationUserData({
                automation_id: automationId,
                page: 1,
                limit: totalCount
            }).unwrap();

            const allData = response?.data || [];

            if (!allData.length) {
                displayToast("error", "No data to download");
                return;
            }

            const csvContent = [
                headers.join(','),
                ...allData.map((row: any) => headers.map(h => {
                    // Handle different key casing for CSV
                    if (h === 'Name') return escapeVal(row.Name || row.name);
                    if (h === 'Email') return escapeVal(row.Email || row.email);
                    if (h === 'Phone Number') return escapeVal(row['Phone Number'] || row.phone_number || row.phoneNumber);
                    if (h === 'Created At') return escapeVal(row.created_at || row.createdAt);
                    return escapeVal(row?.[h]);
                }).join(','))
            ].join('\n');

            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            const dateStr = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
            a.download = `leads-${automationId}-${dateStr}.csv`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);

            displayToast("success", `Downloaded ${allData.length} leads successfully`);
        } catch (err: any) {
            console.error('Failed to download CSV', err);
            displayToast("error", err?.data?.message || "Failed to download CSV");
        } finally {
            setIsDownloading(false);
        }
    };

    const totalPages = Math.max(1, Math.ceil(totalCount / limit));

    const handlePageChange = (newPage: number) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setPage(newPage);
        }
    }

    return (
        <ReactModal
            isOpen={isOpen}
            onRequestClose={onClose}
            className="relative max-w-4xl w-[95%] max-h-[85vh] flex flex-col rounded-xl border-none bg-white outline-none"
            overlayClassName="z-50 fixed inset-0 bg-black/40 flex items-center justify-center p-2"
            ariaHideApp={false}
        >
            <div className="rounded sticky top-0 z-10 bg-white border-b p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <h2 className="text-base sm:text-lg md:text-xl font-semibold">Automation Leads</h2>
                <div className="flex w-full sm:w-auto gap-2">
                    <button
                        onClick={downloadCSV}
                        disabled={data.length === 0 || isDownloading}
                        className="w-full sm:w-auto px-3 py-1.5 text-sm border border-[#3535d5] text-[#3535d5] rounded hover:bg-[#3535d5] hover:text-white disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                        {isDownloading && (
                            <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                        )}
                        {isDownloading ? 'Downloading...' : 'Download CSV'}
                    </button>
                    <button
                        onClick={onClose}
                        className="w-full sm:w-auto px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-100"
                    >
                        Close
                    </button>
                </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 min-h-[300px]">
                {isLoading ? (
                    <div className="flex justify-center items-center h-[200px]">
                        <Loader isLoading={true} />
                    </div>
                ) : (
                    <div className="overflow-x-auto border rounded-md">
                        <table className="min-w-full text-sm">
                            <thead className="bg-gray-50 sticky top-0 z-10">
                                <tr>
                                    {headers.map((h) => (
                                        <th key={h} className="px-4 py-3 text-left font-semibold text-gray-700 border-b bg-gray-50 whitespace-nowrap">{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {data.length > 0 ? (
                                    data.map((row: any, idx: number) => (
                                        <tr key={idx} className="hover:bg-gray-50 transition-colors">
                                            <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{row.Username || row.username || '-'}</td>
                                            <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{row.Name || row.name || '-'}</td>
                                            <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{row.Email || row.email || '-'}</td>
                                            <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{row['Phone Number'] || row.phone_number || row.phoneNumber || '-'}</td>
                                            <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{row.created_at ? new Date(row.created_at).toLocaleString() : (row.createdAt ? new Date(row.createdAt).toLocaleString() : '-')}</td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={4} className="px-4 py-8 text-center text-gray-500">
                                            No leads found for this automation.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <div className="sticky bottom-0 bg-white z-10 px-4 py-3 border-t flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="text-sm text-gray-600">
                    Showing {data.length > 0 ? (page - 1) * limit + 1 : 0} to {Math.min(page * limit, totalCount)} of {totalCount} entries
                </div>

                <Pagination className="justify-end w-auto mx-0">
                    <PaginationContent>
                        <PaginationItem>
                            <PaginationPrevious
                                onClick={() => handlePageChange(page - 1)}
                                className={page <= 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                            />
                        </PaginationItem>

                        <PaginationItem>
                            <PaginationLink isActive>{page}</PaginationLink>
                        </PaginationItem>

                        <PaginationItem>
                            <PaginationNext
                                onClick={() => handlePageChange(page + 1)}
                                className={page >= totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
                            />
                        </PaginationItem>
                    </PaginationContent>
                </Pagination>
            </div>
        </ReactModal>
    );
};

export default UserInputsModal;
